import { Typography } from '@mui/material';
import { graphql } from '../gql';
import { useModalContext } from '../modal/ModalContext';
import { ModalActions } from '../modal/components/ModalActions';
import { ModalContent } from '../modal/components/ModalContent';
import { useApiMutation } from '../query/hooks';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { z } from 'zod';
import { useSnackbar } from '../store/snackbar';

const journey_DuplicateActivity = graphql(`
	mutation journey_activityDuplicate($id: UUID!) {
		createActivityDeepClone(activityId: $id) {
			ok
			errorMessage
		}
	}
`);

const useDuplicateActivity = () => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();
	const { closeModal } = useModalContext();

	const { mutate } = useApiMutation(journey_DuplicateActivity, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.createActivityDeepClone.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'activity.success.duplicate' }),
					error: intl.formatMessage({ id: 'activity.error.duplicate' }),
				},
				callback: () => closeModal(),
				errorMessage: result.createActivityDeepClone.errorMessage,
				openSnackbar,
			}),
	});

	return { mutate };
};

export const duplicateActivityModalModalSchema = z.object({
	id: z.string(),
});

export const DuplicateActivityModal: FC<z.infer<typeof duplicateActivityModalModalSchema>> = ({ id }) => {
	const intl = useIntl();
	const { mutate: duplicateActivity } = useDuplicateActivity();
	const { closeModal } = useModalContext();

	return (
		<>
			<ModalContent title={intl.formatMessage({ id: 'activity.duplication-modal.title' })}>
				<Typography textAlign={'center'}>
					<strong>
						<FormattedMessage id={'activity.duplication-modal.description'} />
					</strong>
				</Typography>
			</ModalContent>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => duplicateActivity({ id })}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.confirm' })}
			/>
		</>
	);
};
